/** Tiny DOM helpers - the game deliberately ships without a UI framework. */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * A menu button.
 *
 * Every menu sits on top of the canvas, and a mousedown that reaches the canvas
 * re-grabs pointer lock - which would hide the cursor mid-click and hand the
 * mouse back to a game nobody asked to return to. So a button swallows its own
 * mousedown rather than trusting whatever overlay it happens to be dropped in.
 *
 * It swallows Enter and Space for the same kind of reason. Those are the keys
 * that press a focused button, and the browser presses it on the keydown -
 * while the game is listening for both on the window, where Enter is a menu's
 * select and Space is a jump. Left alone, the game queues its own reading and
 * acts on it a frame later, by which time this button has already been pressed
 * and the menu it was on is gone: pressing VALMIS with Enter closed the
 * settings page and then re-opened it through the front page's highlight,
 * which from the outside looks exactly like Enter doing nothing.
 *
 * `hint` is for the key that does the same thing, shown right-aligned in the
 * button: the menu teaches the shortcut instead of replacing it.
 */
export function button(
  label: string,
  onClick: () => void,
  className = 'btn',
  hint?: string,
): HTMLButtonElement {
  const node = el('button', className);
  node.type = 'button';
  node.append(el('span', 'btn__label', label));
  if (hint) node.append(el('span', 'btn__key', hint));
  node.addEventListener('mousedown', (event) => event.stopPropagation());
  node.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
  });
  node.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return node;
}

export function setHidden(node: HTMLElement, hidden: boolean): void {
  node.classList.toggle('is-hidden', hidden);
}

export function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}
